(() => {
  const DAY = 24 * 60 * 60 * 1000;
  const generalI18n = window.BivaltaI18n?.general || {};

  function isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function storageKey(root, device) {
    return `theme_lightbox_${root.dataset.sectionId}_${device}_v1`;
  }

  function getFrequency(root, device) {
    return root.dataset[device === 'mobile' ? 'mobileFrequency' : 'desktopFrequency'] || 'daily';
  }

  function allowedByFrequency(root, device) {
    if (root.dataset.designMode === 'true') return true;

    const frequency = getFrequency(root, device);
    if (frequency === 'every') return true;

    const key = storageKey(root, device);

    try {
      if (frequency === 'session') {
        return sessionStorage.getItem(key) !== 'shown';
      }

      const raw = localStorage.getItem(key);
      if (!raw) return true;

      if (frequency === 'once') return false;

      const last = Number(raw);
      if (!Number.isFinite(last)) return true;

      const age = Date.now() - last;
      const limits = {
        daily: DAY,
        '3days': DAY * 3,
        '7days': DAY * 7,
        '30days': DAY * 30
      };

      return age >= (limits[frequency] || DAY);
    } catch (_) {
      return true;
    }
  }

  function markShown(root, device) {
    if (root.dataset.designMode === 'true') return;

    const frequency = getFrequency(root, device);
    if (frequency === 'every') return;

    const key = storageKey(root, device);

    try {
      if (frequency === 'session') {
        sessionStorage.setItem(key, 'shown');
      } else {
        localStorage.setItem(key, String(Date.now()));
      }
    } catch (_) {}
  }

  function init(root) {
    if (!root || root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const dialog = root.querySelector('[data-lightbox-dialog]');
    const closeButton = root.querySelector('[data-lightbox-close-button]');
    const overlay = root.querySelector('[data-lightbox-close]');
    const copyButtons = [...root.querySelectorAll('[data-lightbox-copy]')];

    if (!dialog) return;

    let opened = false;
    let previousFocus = null;
    let cleanupTrigger = null;

    const device = isMobile() ? 'mobile' : 'desktop';
    const enabled = root.dataset[device === 'mobile' ? 'mobileEnabled' : 'desktopEnabled'] === 'true';

    if (!enabled && root.dataset.designMode !== 'true') return;
    if (!allowedByFrequency(root, device)) return;

    function focusables() {
      return [...dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )].filter(el => !el.hasAttribute('hidden') && el.offsetParent !== null);
    }

    function onKeydown(event) {
      if (event.key === 'Escape' && root.dataset.closeEscape === 'true') {
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const items = focusables();
      if (!items.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function open() {
      if (opened) return;
      opened = true;
      previousFocus = document.activeElement;

      root.hidden = false;
      document.documentElement.classList.add('lightbox-open');
      document.body.classList.add('lightbox-open');

      requestAnimationFrame(() => {
        root.classList.add('is-open');
        const items = focusables();
        (closeButton || items[0] || dialog).focus({ preventScroll: true });
      });

      markShown(root, device);
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      if (!opened) return;
      opened = false;

      root.classList.remove('is-open');
      document.documentElement.classList.remove('lightbox-open');
      document.body.classList.remove('lightbox-open');
      document.removeEventListener('keydown', onKeydown);

      window.setTimeout(() => {
        root.hidden = true;
        if (previousFocus && typeof previousFocus.focus === 'function') {
          previousFocus.focus({ preventScroll: true });
        }
      }, 220);
    }

    function setupTrigger() {
      if (root.dataset.designMode === 'true') {
        window.setTimeout(open, 250);
        return;
      }

      const trigger = root.dataset[device === 'mobile' ? 'mobileTrigger' : 'desktopTrigger'] || 'delay';

      if (trigger === 'immediate') {
        open();
        return;
      }

      if (trigger === 'delay') {
        const delay = Number(root.dataset[device === 'mobile' ? 'mobileDelay' : 'desktopDelay']) || 0;
        const timer = window.setTimeout(open, delay * 1000);
        cleanupTrigger = () => window.clearTimeout(timer);
        return;
      }

      if (trigger === 'scroll') {
        const threshold = Number(root.dataset[device === 'mobile' ? 'mobileScroll' : 'desktopScroll']) || 40;

        const onScroll = () => {
          const doc = document.documentElement;
          const max = Math.max(1, doc.scrollHeight - window.innerHeight);
          const percent = (window.scrollY / max) * 100;

          if (percent >= threshold) {
            window.removeEventListener('scroll', onScroll);
            open();
          }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        cleanupTrigger = () => window.removeEventListener('scroll', onScroll);
        return;
      }

      if (trigger === 'exit' && device === 'desktop') {
        const onExit = event => {
          if (event.clientY <= 8 && !event.relatedTarget) {
            document.removeEventListener('mouseout', onExit);
            open();
          }
        };

        document.addEventListener('mouseout', onExit);
        cleanupTrigger = () => document.removeEventListener('mouseout', onExit);
      }
    }

    closeButton?.addEventListener('click', close);

    overlay?.addEventListener('click', () => {
      if (root.dataset.closeOverlay === 'true') close();
    });

    copyButtons.forEach(button => {
      button.addEventListener('click', async () => {
        const wrap = button.closest('.theme-lightbox__discount');
        const code = wrap?.querySelector('[data-lightbox-code]')?.textContent?.trim();
        if (!code) return;

        try {
          await navigator.clipboard.writeText(code);
        } catch (_) {
          const area = document.createElement('textarea');
          area.value = code;
          area.setAttribute('readonly', '');
          area.style.position = 'fixed';
          area.style.opacity = '0';
          document.body.appendChild(area);
          area.select();
          document.execCommand('copy');
          area.remove();
        }

        const original = button.dataset.copyLabel || button.textContent;
        button.textContent = button.dataset.copiedLabel || generalI18n.copied || original;

        window.setTimeout(() => {
          button.textContent = original;
        }, 1500);
      });
    });

    setupTrigger();

    root.addEventListener('shopify:section:unload', () => {
      cleanupTrigger?.();
      document.removeEventListener('keydown', onKeydown);
    });
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-lightbox-popup]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', event => initAll(event.target));
  document.addEventListener('shopify:section:select', event => {
    const root = event.target.matches?.('[data-lightbox-popup]')
      ? event.target
      : event.target.querySelector?.('[data-lightbox-popup]');

    if (root) {
      root.dataset.designMode = 'true';
      root.dataset.initialized = 'false';
      init(root);
    }
  });
})();
